/**
 * Study Registry - Central Definition System
 *
 * Provides a clean API for defining studies with automatic filtering
 * of common questions and validation.
 */

import { Study, StudyDefinition } from "../types";
import { PreScreeningQuestion } from "../../../types/landingPage";

/**
 * Define a new study with automatic filtering of common questions
 *
 * This function:
 * 1. Takes raw study definition with all questions
 * 2. Filters out common questions automatically
 * 3. Returns a processed Study ready for use
 *
 * @example
 * ```ts
 * const myStudy = defineStudy({
 *   id: 'my-study',
 *   name: 'My Study Name',
 *   trialId: 'TRIAL123',
 *   priority: 1,
 *   eligibleZipCodes: ['10001', '10002'],
 *   questions: allQuestions, // Including common questions
 *   commonQuestionIds: ['age_eligibility', 'concurrent_trial'],
 * });
 * ```
 */
export function defineStudy(definition: StudyDefinition): Study {
  // Filter out common questions from the study-specific questions
  const filteredQuestions = definition.questions.filter(
    (q) => !definition.commonQuestionIds.includes(q.id)
  );

  // Validate that we actually filtered something
  if (
    filteredQuestions.length === definition.questions.length &&
    definition.commonQuestionIds.length > 0
  ) {
    console.warn(
      `[defineStudy] No common questions were filtered for study "${definition.id}". ` +
        `Expected to filter: ${definition.commonQuestionIds.join(", ")}`
    );
  }

  return {
    id: definition.id,
    name: definition.name,
    trialId: definition.trialId,
    priority: definition.priority,
    eligibleZipCodes: definition.eligibleZipCodes,
    questions: filteredQuestions,
    commonAnswerRequirements: definition.commonAnswerRequirements,
    metadata: definition.metadata,
  };
}

/**
 * Helper: Filter questions by excluding specific IDs
 * Useful for manual filtering when needed
 */
export function filterQuestions(
  questions: PreScreeningQuestion[],
  excludeIds: string[]
): PreScreeningQuestion[] {
  return questions.filter((q) => !excludeIds.includes(q.id));
}

/**
 * Helper: Validate that a study definition is complete and correct
 */
export function validateStudy(study: Study): { valid: boolean; errors: string[] } {
  const errors: string[] = [];

  if (!study.id || study.id.trim() === "") {
    errors.push("Study ID is required");
  }

  if (!study.name || study.name.trim() === "") {
    errors.push("Study name is required");
  }

  if (!study.trialId) {
    errors.push("Trial ID is required");
  }

  if (typeof study.priority !== "number" || study.priority < 1) {
    errors.push("Priority must be a positive number");
  }

  if (!Array.isArray(study.eligibleZipCodes) || study.eligibleZipCodes.length === 0) {
    errors.push("At least one eligible zipcode is required");
  }

  if (!Array.isArray(study.questions)) {
    errors.push("Questions must be an array");
  }

  return {
    valid: errors.length === 0,
    errors,
  };
}

/**
 * Create a study registry from multiple study definitions
 */
export class StudyRegistry {
  private studies: Map<string, Study> = new Map();

  /**
   * Register a new study
   */
  register(definition: StudyDefinition): this {
    const study = defineStudy(definition);
    const validation = validateStudy(study);

    if (!validation.valid) {
      throw new Error(
        `Invalid study definition for "${definition.id}":\n` +
          validation.errors.map((e) => `  - ${e}`).join("\n")
      );
    }

    if (this.studies.has(study.id)) {
      console.warn(
        `[StudyRegistry] Study "${study.id}" is already registered. Overwriting...`
      );
    }

    this.studies.set(study.id, study);
    return this;
  }

  /**
   * Get a study by ID
   */
  getStudy(id: string): Study | undefined {
    return this.studies.get(id);
  }

  /**
   * Get all registered studies
   */
  getAllStudies(): Study[] {
    return Array.from(this.studies.values());
  }

  /**
   * Get studies sorted by priority
   */
  getStudiesByPriority(): Study[] {
    return this.getAllStudies().sort((a, b) => a.priority - b.priority);
  }

  /**
   * Clear all registered studies (useful for testing)
   */
  clear(): void {
    this.studies.clear();
  }

  /**
   * Get number of registered studies
   */
  get count(): number {
    return this.studies.size;
  }
}
